// ============================================================
// MIDAS Action Registry — compile-time whitelist of executable actions.
// The registry maps action_type strings to execution functions.
// No dynamic endpoint construction — fail closed on unknown types.
// ============================================================

/** Result returned by an action executor after execution */
export interface ActionResult {
  success: boolean;
  entityId?: string;
  entityNumber?: string;
  message: string;
  link?: string;
  data?: Record<string, any>;
}

/** Context passed to action executors (machineId, threadId, etc.) */
export interface ActionExecutionContext {
  machineId?: string;
  threadId?: string;
}

/** A single executable action definition */
export interface ActionExecutor {
  type: string;
  module: string;
  requiredPermission: "Read" | "Write" | "Admin";
  approvalTier: 0 | 1 | 2 | 3;
  execute: (values: Record<string, any>, context?: ActionExecutionContext) => Promise<ActionResult>;
  undoable?: boolean;
  undo?: (result: ActionResult) => Promise<void>;
}

/** Schema field definition (for dynamic form rendering) */
export interface ActionSchemaField {
  name: string;
  type: "text" | "textarea" | "dropdown" | "file" | "date" | "number" | "currency" | "readonly";
  label: string;
  description?: string;
  required?: boolean;
  max_length?: number;
  min?: number;
  max?: number;
  options?: Array<{ value: string; label: string }>;
  accept?: string[];
  max_size?: number;
  display_hints?: {
    emphasis?: "normal" | "high" | "critical";
    kind?: string;
    group?: string;
    help_text?: string;
    confirmation_level?: "none" | "inline" | "modal";
    dependency?: { field: string; value: string };
  };
}

/** Schema for an action (defines form fields + display) */
export interface ActionSchema {
  action_type: string;
  module: string;
  required_permission: string;
  approval_tier: number;
  display: {
    title: string;
    icon?: string;
  };
  fields: ActionSchemaField[];
}

/** Action request received from bot (via SSE or intercepted from actions[]) */
export interface ActionRequest {
  action_id: string;
  action_type: string;
  schema: ActionSchema;
  values: Record<string, any>;
  display: {
    title: string;
    summary?: string;
    icon?: string;
  };
  approval_tier: number;
}

/** State of an action in the UI */
export type ActionState = "pending" | "executing" | "success" | "error";

/** Full action status tracked in Redux */
export interface ActionStatus {
  actionId: string;
  actionType: string;
  state: ActionState;
  result?: ActionResult;
  error?: string;
  createdAt: number; // timestamp for staleness check
}

// ============================================================
// SEC-REQ-4: Explicit blocklist — checked BEFORE registry lookup.
// Fail closed: blocked types are rejected immediately.
// ============================================================

export const BLOCKED_ACTION_TYPES = [
  "admin.delete_account",
  "admin.manage_users",
  "admin.change_permissions",
  "admin.company_settings",
  "admin.delete_company",
] as const;

/** Check if an action type is explicitly blocked (SEC-REQ-4) */
export const isBlockedActionType = (actionType: string): boolean => {
  return (BLOCKED_ACTION_TYPES as readonly string[]).includes(actionType);
};

// ============================================================
// Registry — populated by actionDefinitions.ts
// ============================================================

const registry: Record<string, ActionExecutor> = {};

/** Register an action executor. Called by actionDefinitions.ts at module load time. */
export const registerAction = (executor: ActionExecutor): void => {
  registry[executor.type] = executor;
};

/**
 * Look up an action executor by type. Fail closed — returns null for unknown types.
 * Checks blocklist first (SEC-REQ-4).
 */
export const getActionExecutor = (actionType: string): ActionExecutor | null => {
  // SEC-REQ-4: blocklist check
  if (isBlockedActionType(actionType)) {
    console.warn(`[ActionRegistry] Blocked action type: ${actionType}`);
    return null;
  }
  return registry[actionType] || null;
};

/** Check if an action type is registered (and not blocked) */
export const isRegisteredAction = (actionType: string): boolean => {
  if (isBlockedActionType(actionType)) return false;
  return actionType in registry;
};

// ============================================================
// Mapping from old bot action IDs to new registry action types.
// Used by MA-0a intercept pattern (ChatActionButtons).
// ============================================================

export const ACTION_ID_TO_TYPE: Record<string, string> = {
  create_sr: "machinery.create_service_report",
  create_service_report: "machinery.create_service_report",
};

/** Map an old bot action ID to a registry action type, or null if not mapped */
export const mapActionIdToType = (actionId: string): string | null => {
  return ACTION_ID_TO_TYPE[actionId] || null;
};

/** SEC-REQ-9: Runtime assertion — Tier 0 requires undoable or Read permission */
export const validateTier0 = (executor: ActionExecutor): boolean => {
  if (executor.approvalTier === 0) {
    if (!executor.undoable && executor.requiredPermission !== "Read") {
      console.error(
        `[ActionRegistry] SEC-REQ-9 violation: Tier 0 action ${executor.type} must be undoable or read-only`
      );
      return false;
    }
  }
  return true;
};

/** Staleness check: actions older than 15 minutes should be dimmed */
export const STALE_ACTION_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
